import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, queryString } from '../api';
import { ErrorNotice, Field, Notice, SelectField, Spinner, formatMoney } from '../components/ui';
import type { JoinPricing, JoinResult, MemberListRow } from '../types';

/**
 * A paper application, typed up by whoever collected it.
 *
 * Laid out as the form on the table is laid out, so somebody can work down a
 * sheet without hunting. It posts to the same code as the public form, so the
 * fee is whatever the applicant would have been quoted online — the running
 * total below is only a repeat of that, and the charge is the server's.
 */
export function AddMemberPage() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState<JoinPricing | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<JoinResult | null>(null);

  const [form, setForm] = useState({
    title: '', first_name: '', last_name: '',
    address_1: '', address_2: '', town: '', postcode: '',
    telephone: '', mobile: '', email: '',
    date_of_birth: '', guardian_name: '', guardian_email: '',
    category_code: '', colony_count: '0', joined_date: new Date().toISOString().slice(0, 10),
  });

  const [partnerOf, setPartnerOf] = useState<MemberListRow | null>(null);
  const [partnerSearch, setPartnerSearch] = useState('');
  const [partnerResults, setPartnerResults] = useState<MemberListRow[]>([]);

  function set(field: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  useEffect(() => {
    api.get<JoinPricing>('/join/pricing').then(setPricing).catch(setError);
  }, []);

  useEffect(() => {
    if (partnerSearch.trim().length < 2) {
      setPartnerResults([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      api
        .get<{ rows: MemberListRow[] }>(`/members${queryString({ q: partnerSearch, per_page: 6 })}`)
        .then((r) => { if (!cancelled) setPartnerResults(r.rows); })
        .catch(() => undefined);
    }, 250);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [partnerSearch]);

  if (pricing === null && error === null) return <Spinner label="Loading this year's rates" />;
  if (pricing !== null && !pricing.open) {
    return (
      <Notice kind="warning">
        No membership year is open, so there is nothing to apply for. Set one up under{' '}
        <Link to="/rates">Rates &amp; settings</Link> first.
      </Notice>
    );
  }

  const open = pricing !== null && pricing.open ? pricing : null;
  const category = open?.categories.find((c) => c.code === form.category_code) ?? null;

  const age = form.date_of_birth === ''
    ? null
    : Math.floor((Date.now() - new Date(form.date_of_birth).getTime()) / 31557600000);
  const isMinor = age !== null && age < 18;

  const colonies = Number(form.colony_count) || 0;
  const band = category?.carries_bdi
    ? open?.bdi_bands.find((b) => b.max_colonies >= colonies) ?? null
    : null;
  const overTopBand = (category?.carries_bdi ?? false) && band === null && colonies > 0;
  const total = (category?.amount ?? 0) + (band?.premium ?? 0);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await api.post<JoinResult>('/members', {
        ...form,
        colony_count: colonies,
        title: form.title || null,
        partner_of_member_id: partnerOf?.id ?? null,
      });
      setDone(result);
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  if (done !== null) {
    return (
      <>
        <div className="page-head">
          <h1>{done.member.first_name} {done.member.last_name} added</h1>
        </div>
        <Notice kind="success">
          Subscription created for {done.subscription.year}: {formatMoney(done.subscription.total_due)} due
          {done.subscription.due_on ? ` by ${done.subscription.due_on}` : ''}. They show as prospective
          until the payment is recorded.
        </Notice>
        <div className="card">
          <div className="actions">
            <Link to={`/members/${done.member.id}`} className="button-link primary">
              Open their record
            </Link>
            <button type="button" onClick={() => { setDone(null); navigate(0); }}>
              Add another
            </button>
            <Link to="/members" className="button-link">Back to members</Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <p className="muted small">
        <Link to="/members">← Back to members</Link>
      </p>

      <div className="page-head">
        <h1>Add a member</h1>
      </div>

      <p className="muted">
        For an application taken on paper. It is charged exactly what the online form would have
        quoted. No password is set — they choose one themselves with the “forgotten password” link.
      </p>

      <ErrorNotice error={error} />

      <form className="card form wide" onSubmit={onSubmit}>
        <div className="grid">
          <Field label="Title" value={form.title} onChange={(e) => set('title', e.target.value)} />
          <Field label="First name" required value={form.first_name} onChange={(e) => set('first_name', e.target.value)} />
          <Field label="Surname" required value={form.last_name} onChange={(e) => set('last_name', e.target.value)} />
        </div>

        <div className="grid">
          <Field label="Address" value={form.address_1} onChange={(e) => set('address_1', e.target.value)} />
          <Field label="Address line 2" value={form.address_2} onChange={(e) => set('address_2', e.target.value)} />
          <Field label="Town" value={form.town} onChange={(e) => set('town', e.target.value)} />
          <Field label="Postcode" value={form.postcode} onChange={(e) => set('postcode', e.target.value)} />
        </div>

        <div className="grid">
          <Field label="Telephone" value={form.telephone} onChange={(e) => set('telephone', e.target.value)} />
          <Field label="Mobile" value={form.mobile} onChange={(e) => set('mobile', e.target.value)} />
          <Field
            label="Email"
            type="email"
            value={form.email}
            hint="Leave blank if they have none — they will be reached by post."
            onChange={(e) => set('email', e.target.value)}
          />
        </div>

        <div className="grid">
          <Field
            label="Date of birth"
            type="date"
            value={form.date_of_birth}
            hint="Needed for Junior membership, and for anybody under 18."
            onChange={(e) => set('date_of_birth', e.target.value)}
          />
          <Field
            label="Joined"
            type="date"
            required
            value={form.joined_date}
            hint="Today for a new member; the original date if you are typing up an old record."
            onChange={(e) => set('joined_date', e.target.value)}
          />
        </div>

        {isMinor ? (
          <>
            <Notice kind="info">
              They are under 18, so a parent or guardian has to consent. This is required whatever
              category they join in.
            </Notice>
            <div className="grid">
              <Field label="Guardian name" required value={form.guardian_name} onChange={(e) => set('guardian_name', e.target.value)} />
              <Field label="Guardian email" type="email" required value={form.guardian_email} onChange={(e) => set('guardian_email', e.target.value)} />
            </div>
          </>
        ) : null}

        <div className="grid">
          <SelectField
            label="Membership category"
            required
            value={form.category_code}
            onChange={(e) => { set('category_code', e.target.value); setPartnerOf(null); }}
          >
            <option value="">Choose…</option>
            {(open?.categories ?? []).map((c) => (
              <option key={c.code} value={c.code}>
                {c.name} — {formatMoney(c.amount)}
              </option>
            ))}
          </SelectField>

          {category?.carries_bdi ? (
            <Field
              label="Colonies"
              type="number"
              min={0}
              value={form.colony_count}
              hint="Sets the bee disease insurance band."
              onChange={(e) => set('colony_count', e.target.value)}
            />
          ) : null}
        </div>

        {/* A Partner Member is attached to a Full Member. The committee names
            them directly; the public form requires an invite link instead,
            because there is no public member search to pick from. */}
        {category?.requires_parent ? (
          <div className="field">
            <label htmlFor="partner-of">Partner of which member</label>
            {partnerOf !== null ? (
              <p>
                <strong>{partnerOf.first_name} {partnerOf.last_name}</strong>
                {partnerOf.town ? <span className="muted"> · {partnerOf.town}</span> : null}{' '}
                <button type="button" className="link" onClick={() => setPartnerOf(null)}>change</button>
              </p>
            ) : (
              <>
                <input
                  id="partner-of"
                  type="search"
                  placeholder="Search by name"
                  value={partnerSearch}
                  onChange={(e) => setPartnerSearch(e.target.value)}
                />
                {partnerResults.length > 0 ? (
                  <ul className="permission-list">
                    {partnerResults.map((row) => (
                      <li key={row.id}>
                        <button type="button" className="link" onClick={() => { setPartnerOf(row); setPartnerSearch(''); }}>
                          {row.first_name} {row.last_name}
                          {row.town ? <span className="muted"> · {row.town}</span> : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            )}
          </div>
        ) : null}

        {category !== null ? (
          <div className="card">
            <dl className="details">
              <div className="detail">
                <dt>{category.name}</dt>
                <dd>{formatMoney(category.amount)}</dd>
              </div>
              {category.carries_bdi ? (
                <div className="detail">
                  <dt>Bee disease insurance</dt>
                  <dd>{overTopBand ? 'referral needed' : formatMoney(band?.premium ?? 0)}</dd>
                </div>
              ) : null}
              <div className="detail">
                <dt>Total</dt>
                <dd><strong>{formatMoney(total)}</strong></dd>
              </div>
            </dl>
            {overTopBand ? (
              <p className="muted small">
                Above the top band, so the premium is set by referral to BDI rather than charged
                here. The subscription is flagged for it.
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="actions">
          <button type="submit" className="primary" disabled={busy || form.category_code === ''}>
            {busy ? 'Adding…' : 'Add member'}
          </button>
          <Link to="/members" className="button-link">Cancel</Link>
        </div>
      </form>
    </>
  );
}
